// src/components/dashboard/AbatementPathwayStackedArea.js
import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler
} from "chart.js";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { getAbatementColor } from "../../config/colorScheme";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const DECOMP_SCENE_QUERY = gql`
  query DecompScene($scenario: String, $from: Int, $to: Int) {
    timeSeries(view: DECOMP_SCENE, scenario: $scenario, fromYear: $from, toYear: $to) {
      year
      group
      value
    }
  }
`;

// Display order (legend): top → bottom
const LEGEND_ORDER = [
  "Electrification",
  "Energy demand reduction",
  "Carbon dioxide removals",
  "Other",
  "Remaining emissions",
];

// Bottom-up stacking order to build cumulative boundaries
const STACK_ORDER = [
  "Remaining emissions",
  "Other",
  "Carbon dioxide removals",
  "Energy demand reduction",
  "Electrification",
];

const norm = (s) => String(s || "").trim().toLowerCase();
const labelFor = (g) => {
  const s = norm(g);
  if (s.startsWith("electr")) return "Electrification";
  if (s.startsWith("energy demand")) return "Energy demand reduction";
  if (s.startsWith("carbon dioxide remov")) return "Carbon dioxide removals";
  if (s.startsWith("remaining emissions")) return "Remaining emissions";
  if (s.startsWith("other")) return "Other";
  return "Other";
};

const COLOR_KEY = {
  "Electrification": "electricityDecarb",
  "Energy demand reduction": "energyEfficiency",
  "Carbon dioxide removals": "engineeredRemovals",
  "Other": "other",
  "Remaining emissions": "remaining",
};

export default function AbatementPathwayStackedArea({
  scenario,
  from = 2025,
  to = 2050,
  step = 5,
  title = "UK route to net zero (2025-2050)",
  className = "w-full h-96 sm:w-5/5 md:w-4/5 lg:w-4/5 xl:w-3/5 align-center m-auto",
}) {
  const { data, loading, error } = useQuery(DECOMP_SCENE_QUERY, {
    variables: { scenario, from, to },
    fetchPolicy: "cache-first",
  });

  const rows = data?.timeSeries ?? [];

  // Year labels (5-year ticks from `from`)
  const labels = useMemo(() => {
    const years = Array.from(new Set(rows.map(r => Number(r.year)).filter(Number.isFinite))).sort((a,b)=>a-b);
    const filtered = years.filter(y => y >= from && y <= to && ((y - from) % step === 0));
    return (filtered.length ? filtered : years).map(String);
  }, [rows, from, to, step]);

  const signedValue = (g, raw) => {
  const L = labelFor(g);
//   return L === "Carbon dioxide removals" ? -Number(raw) : Number(raw);
    return Number(raw)
};
  // year -> { wedge -> value }
  const byYear = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const y = Number(r.year); if (!Number.isFinite(y)) continue;
      const w = labelFor(r.group);
      const v = signedValue(r.group, (Number(r.value) || 0) / 1000);
    //   const v = (Number(r.value) || 0) / 1000;
    //   const v = Number(r.value) || 0;
      if (!m.has(y)) m.set(y, {});
      m.get(y)[w] = (m.get(y)[w] || 0) + v;
    }
    // ensure all wedges present
    for (const y of m.keys()) for (const w of STACK_ORDER) if (!(w in m.get(y))) m.get(y)[w] = 0;
    return m;
  }, [rows]);

  // Build cumulative boundaries (bottom-up like Excel stacked area)
  const boundaries = useMemo(() => {
    const result = {}; // wedge -> array of cumulative values
    // start with zeros
    const zero = labels.map(() => 0);

    let prev = zero;
    for (const w of STACK_ORDER) {
      const series = labels.map((ys, i) => {
        const y = Number(ys);
        const val = byYear.get(y)?.[w] ?? 0;
        return prev[i] + val;
      });
      result[w] = series;
      prev = series; // next wedge stacks on top
    }
    return result;
  }, [labels, byYear]);

  const datasets = useMemo(() => {
    const ds = [];

    // Build in the same order we stacked so fills line up
    for (let i = 0; i < STACK_ORDER.length; i++) {
      const w = STACK_ORDER[i];
      const color = getAbatementColor(COLOR_KEY[w], "primary") || "#999";
      const fillColor = getAbatementColor(COLOR_KEY[w], "light") || "rgba(0,0,0,0.12)";

      ds.push({
        label: w,
        data: boundaries[w],
        borderColor: color,
        backgroundColor: fillColor,
        fill: i === 0 ? "origin" : "-1", // first fills to origin; others fill to previous
        tension: 0.25,
        pointRadius: 0,
        spanGaps: true,
        order: i, // bottom to top
      });
    }

    return ds;
  }, [boundaries]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      title: { display: true, text: title },
      legend: {
        position: "right",
        labels: {
          usePointStyle: true,
          padding: 12,
          sort: (a, b) => LEGEND_ORDER.indexOf(a.text) - LEGEND_ORDER.indexOf(b.text),
        },
      },
      tooltip: {
        callbacks: {
          // Show incremental wedge value at the point (not the cumulative boundary)
          label: (ctx) => {
            const i = ctx.dataIndex;
            const dsIdx = ctx.datasetIndex;
            const cur = Number(ctx.dataset.data[i] ?? 0);
            const prev = dsIdx === 0 ? 0 : Number(ctx.chart.data.datasets[dsIdx - 1].data[i] ?? 0);
            const inc = cur - prev;
            return `${ctx.dataset.label}: ${inc.toLocaleString("en-GB", { maximumFractionDigits: 0 })} MtCO₂e`;
          },
          afterBody: (items) => {
            const i = items?.[0]?.dataIndex ?? null;
            if (i == null) return [];
            // total = top boundary (last dataset)
            const dsets = items[0].chart.data.datasets;
            const total = dsets[dsets.length - 1].data[i];
            return [`Total: ${Number(total).toLocaleString("en-GB")} MtCO₂e`];
          },
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year" } },
      y: {
        title: { display: true, text: "Emissions (MtCO₂e)" },
        beginAtZero: true,
        suggestedMin: 0, // keep 0 visible even if negatives exist
        ticks: { callback: (v) => Number(v).toLocaleString("en-GB") },
        grid: { color: "rgba(0,0,0,0.08)" },
      },
    },
    elements: { line: { borderWidth: 2 }, point: { radius: 0, hitRadius: 6 } },
  };

  const isEmpty = !labels.length || !datasets.length;

  return (
    <div className="w-full">
      <div className={className}>
        {loading ? (
          <div className="w-full h-full grid place-items-center text-xs text-gray-500">Loading…</div>
        ) : error ? (
          <div className="w-full h-full grid place-items-center text-red-600">Error: {error.message}</div>
        ) : isEmpty ? (
          <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">No data.</div>
        ) : (
          <Line data={{ labels, datasets }} options={options} />
        )}
      </div>
    </div>
  );
}
